import React from "react";
import { scaleLinear } from "d3-scale";
import {
  INITIAL_STATE,
  DIALYSIS_PATH_MIN_HEIGHT,
  COL_TYPE,
  CELL_TYPE,
  DIALYSIS_DRAG_DROP,
} from "./DialysisDragDrop.constants";

export function getScale(state) {
  return scaleLinear()
    .domain([state.rangeBottom, state.rangeTop])
    .range([DIALYSIS_PATH_MIN_HEIGHT, state.dialysisPathHeight]);
}

export const DialysisDragDropContext = React.createContext(null);

export const DialysisTableRef = React.createRef(null);

export function getTop(scaleValue, state) {
  return state.dialysisPathHeight - scaleValue + state.dialysisPathPadding;
}

export function useDialysisDragDrop(props) {
  const [state, setState] = React.useState({
    ...INITIAL_STATE,
    rangeTop: props.dialysisRange.top,
    rangeBottom: props.dialysisRange.bottom,
  });
  const [topValues, setTopValues] = React.useState({ min: 1, max: 1 });

  // To calculate and set top values whenever state changes.
  React.useEffect(() => {
    const scale = getScale(state);
    const min = getTop(scale(state.rangeTop), state);
    const max = getTop(scale(state.rangeBottom), state);
    setTopValues({ min, max });
  }, [state.dialysisPathHeight]);

  // function to get the max number of Dialysis tubes in any holders of table
  const maxDialysisItemsPerHolder = React.useMemo(
    () => getMaxDialysisItemsPerHolder(props.dialysisTable),
    []
  );

  // Function to calculate Dialysis-Table Dimensions
  function calculateHeight(tableRef) {
    const { offsetWidth } = tableRef;
    const newState = { ...state };
    newState.dialysisPathHeight = getDialysisPathHeight(offsetWidth);
    newState.dialysisHolderBoxHeight = getDialysisBoxHeight(
      newState.dialysisPathHeight
    );
    newState.dialysisPathPadding = getDialysisPathPaddingBottom();
    setState(newState);
  }

  // Function to allow dragging over other elements
  const onDragOver = (event) => {
    event.preventDefault();
  };

  // Function to calculate new-dialysis-value
  const getDialysisValue = ({ currentTarget, clientY }) => {
    if (!currentTarget) return;
    const { top } = currentTarget.getBoundingClientRect();
    const scale = getScale(state);
    let topCurrent = clientY - top;
    if (topCurrent < topValues.min) topCurrent = topValues.min;
    if (topCurrent > topValues.max) topCurrent = topValues.max;
    const linearCurrent = getValue(topCurrent, state);
    const valuecurrent = scale.invert(linearCurrent);
    return valuecurrent;
  };

  // Function to invoke on DialysisStripDrop
  const onStripDrop = ({ id, event, value, cellId, label }) => {
    const [colId, prevCellId, dialysisId] = splitIDs(
      event.dataTransfer.getData(DIALYSIS_DRAG_DROP.DIALYSIS_ID)
    );
    const [currentColId, currentCellId] = splitIDs(cellId);
    if (colId === currentColId) {
      props.onDialysisUpdate({
        id,
        type: "predicts",
        colId,
        prevCellId,
        cellId: currentCellId,
        dialysisId,
        value,
        label,
      });
    }
  };

  // Function to invoke when DialysisBand is moved by keyboard
  const onStripChange = ({
    id,
    colId,
    prevCellId,
    dialysisId,
    currentCellId,
    value,
  }) => {
    props.onDialysisUpdate({
      id,
      type: "predicts",
      colId,
      prevCellId,
      cellId: currentCellId,
      dialysisId,
      value,
    });
  };

  // Function to set style grid templateRows in column componnets
  const getGridTemplateRows = (colItemsLength) => {
    let value = "";
    for (let i = 0; i < colItemsLength; i++) {
      value += "auto ";
    }
    return { gridTemplateRows: value };
  };

  // Variable with a list of all clipable values
  const clipValues = React.useMemo(() => {
    let scaleValues = [];
    if (props.dialysisTable[0].colType === COL_TYPE.SCALE_COLUMN) {
      scaleValues = props.dialysisTable[0].colItems[1].cellItems
        .map((dialysis) => dialysis.value)
        .sort((a, b) => a - b);
    }
    return scaleValues.flatMap((value, index) => {
      if (index >= 1) {
        return [value];
      }
      return [value];
    });
  }, []);

  // Funtion to invoke when a dialysis band is clicked
  const onDialysisBandClick = (idString, componentId) => {
    const [colId, cellId, dialysisId] = splitIDs(idString);
    props.onDialysisSelect({
      colId,
      cellId,
      dialysisId,
      type: "predicts",
      id: componentId,
    });
  };

  return {
    state,
    clipValues,
    isMultiDialysisHolder: maxDialysisItemsPerHolder > 1,
    calculateHeight,
    onDragOver,
    getDialysisValue,
    onStripDrop,
    getGridTemplateRows,
    onStripChange,
    onDialysisBandClick,
  };
}

function getDialysisPathHeight(offsetWidth) {
  return offsetWidth / 1.15;
}

function getDialysisBoxHeight(dialysisPathHeight) {
  return dialysisPathHeight * 0.03;
}

function getDialysisPathPaddingBottom() {
  return 30;
}

function getMaxDialysisItemsPerHolder(dialysisTable) {
  return dialysisTable.reduce((max, column) => {
    if (column.colType === COL_TYPE.BAND_COLUMN) {
      const newMax = column.colItems.reduce((maxItems, cell) => {
        if (cell.cellType === CELL_TYPE.DIALYSIS_HOLDER) {
          maxItems = cell.cellItems.length;
        }
        return maxItems;
      }, 0);
      if (newMax > max) {
        max = newMax;
      }
    }
    return max;
  }, 1);
}

export function getTopValue(scaleValue, state) {
  return state.dialysisPathHeight - scaleValue + state.dialysisPathPadding;
}

function getValue(topValue, state) {
  return getTopValue(topValue, state);
}

export function splitIDs(idString) {
  return idString.split("_").map((numString) => parseInt(numString));
}

const CELL_IDS = {
  0: CELL_TYPE.DIALYSIS_HOLDER,
  1: CELL_TYPE.DIALYSIS_PATH,
};

export function getCellTypeFromID(dialysisId) {
  const cellId = splitIDs(dialysisId)[1];
  return { cellType: CELL_IDS[cellId], cellId };
}

export function getNextClipValue(clipValues, currentValue) {
  const index = clipValues.findIndex((clipValue) => clipValue === currentValue);
  return clipValues[index + 1];
}
export function getPreviousClipValue(clipValues, currentValue) {
  const index = clipValues.findIndex((clipValue) => clipValue === currentValue);
  return clipValues[index - 1];
}
